
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  TreePine, 
  Calendar, 
  Users, 
  Plus, 
  LayoutDashboard, 
  Bell, 
  Settings, 
  Search,
  CheckCircle2,
  Clock,
  LogOut,
  Briefcase,
  MessageSquare,
  ChevronRight,
  Edit,
  CheckCircle,
  Slash,
  Trash2,
  AlertTriangle,
  MapPin,
  DollarSign,
  Award,
  TrendingUp,
  UserPlus,
  ArrowUpRight,
  Star,
  Share2,
  Download,
  Camera,
  Image as ImageIcon,
  X,
  ShieldCheck,
  Zap,
  FileText,
  ShieldAlert,
  ExternalLink,
  Upload,
  FolderOpen,
  Phone,
  Mail,
  Fingerprint,
  CalendarDays,
  Bot,
  Globe,
  HardHat,
  UserCheck,
  Lock
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { UserRole, Job, UserProfile, JobStatus, SkillType, Conversation, Message, Review, License, LocalRequirement, PortfolioProject, VerificationType, Vouch, ForumPost, ForumComment, TeamMember } from './types';
import { JobCard, JobCalendar, JobForm, ChatWindow, ReviewForm, LicenseUploadModal, PortfolioProjectModal, VouchModal, AiAssistant, Forum } from './components';
import { analyzeJobMatch } from './services/geminiService';
import { researchLocalRequirements } from './services/licensingService';

import { useFirebase } from '../context/FirebaseContext';
import { db, auth } from '../firebase';
import { collection, onSnapshot, addDoc, updateDoc, doc, setDoc, arrayUnion, arrayRemove, getDocs } from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '../lib/firestore-errors';

const ASSETS = {
  LOGO_MONKEY: "https://i.ibb.co/LhygX9t/logo-monkey.png",
  LOGO_3D: "https://i.ibb.co/m0fHn9L/logo-3d.jpg",
  QR_RECRUIT: "https://i.ibb.co/0VvS6L0/qr-wood.png"
};

const INITIAL_TEAM: TeamMember[] = [
  {
    id: 'tm1',
    name: 'Sarah "The Saw" Miller',
    avatar: 'https://picsum.photos/seed/sarah/100/100',
    skills: [SkillType.Climber, SkillType.CraneOperator],
    assignedJobIds: ['1'],
    availability: [
      { date: '2024-07-12', status: 'BUSY', title: 'Heritage Oak' },
      { date: '2024-07-13', status: 'AVAILABLE' }
    ]
  },
  {
    id: 'tm2',
    name: 'Marcus Chen',
    avatar: 'https://picsum.photos/seed/marcus/100/100',
    skills: [SkillType.Groundsman, SkillType.StumpGrinder],
    assignedJobIds: ['1'],
    availability: [
      { date: '2024-07-12', status: 'BUSY', title: 'Heritage Oak' },
      { date: '2024-07-14', status: 'AVAILABLE' }
    ]
  }
];

const INITIAL_WORKER: UserProfile = {
  id: 'worker1',
  name: 'Alex Rivera',
  role: UserRole.WORKER,
  email: 'alex@treepro.com',
  skills: [SkillType.Climber, SkillType.CraneOperator],
  certifications: ['ISA Certified Arborist', 'EHAP'],
  licenses: [],
  verifications: [VerificationType.EMAIL, VerificationType.PHONE, VerificationType.LICENSE],
  portfolio: [
    {
      id: 'p1',
      title: 'Technical Removal: 80ft Water Oak',
      description: 'Complex removal over a residential pool. Used high-angle rigging and technical climbing methods. No property damage.',
      imageUrl: 'https://images.unsplash.com/photo-1596708053420-139a044077d7?auto=format&fit=crop&q=80&w=800',
      date: '2024-05-15'
    }
  ],
  vouches: [
    {
      id: 'v1',
      skill: SkillType.Climber,
      vouchedById: 'comp2',
      vouchedByName: 'Phenix City Arbor Pros',
      comment: 'Alex is one of the safest climbers I have ever worked with. His technical rigging is flawless.',
      timestamp: '2024-06-01T10:00:00Z'
    }
  ],
  bio: 'Experienced climber with 8 years in the industry. Specialist in technical removals and structural pruning in the Chattahoochee Valley area.',
  rating: 4.9,
  avatar: 'https://picsum.photos/seed/alex/150/150',
  location: 'Columbus, GA',
  following: []
};

const INITIAL_EMPLOYER: UserProfile = {
  id: 'comp1',
  name: 'GreenTree Arboriculture',
  role: UserRole.EMPLOYER,
  email: 'ops@greentree.com',
  skills: [],
  certifications: [],
  licenses: [],
  verifications: [VerificationType.EMAIL, VerificationType.PHONE, VerificationType.IDENTITY],
  bio: 'ArborLink is a service provided and powered by GreenTree Arboriculture. We are technical removal specialists serving the Southeast.',
  rating: 4.8,
  avatar: ASSETS.LOGO_MONKEY,
  banner: ASSETS.LOGO_3D,
  location: 'Columbus, GA',
  following: [],
  team: INITIAL_TEAM
};



const VerificationBadge: React.FC<{ type: VerificationType }> = ({ type }) => {
  const icons = {
    [VerificationType.EMAIL]: <Mail className="w-3 h-3" />,
    [VerificationType.PHONE]: <Phone className="w-3 h-3" />,
    [VerificationType.LICENSE]: <ShieldCheck className="w-3 h-3" />,
    [VerificationType.IDENTITY]: <Fingerprint className="w-3 h-3" />
  };

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-[9px] font-black uppercase tracking-widest border border-emerald-100 shadow-sm" title={`${type} Verified`}>
      {icons[type]}
      {type}
    </div>
  );
};

export const ArborLinkApp: React.FC = () => {
  const [role, setRole] = useState<UserRole>(UserRole.WORKER);
  const [view, setView] = useState<'dashboard' | 'calendar' | 'jobs' | 'profile' | 'messages' | 'manage-jobs' | 'community' | 'team'>('dashboard');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [forumPosts, setForumPosts] = useState<ForumPost[]>([]);
  const [appliedJobIds, setAppliedJobIds] = useState<string[]>([]);
  const [activeAnalysis, setActiveAnalysis] = useState<{jobId: string, result: any} | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isJobFormOpen, setIsJobFormOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | undefined>(undefined);
  
  const [employerProfile, setEmployerProfile] = useState<UserProfile>(INITIAL_EMPLOYER);
  const [workerProfile, setWorkerProfile] = useState<UserProfile>(INITIAL_WORKER);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [isPortfolioModalOpen, setIsPortfolioModalOpen] = useState(false);
  const [isVouchModalOpen, setIsVouchModalOpen] = useState(false);
  const [isDocumentVaultOpen, setIsDocumentVaultOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentType, setDocumentType] = useState<'certification' | 'license' | 'other'>('certification');
  const [documentFile, setDocumentFile] = useState<string | null>(null);
  const [documentFileName, setDocumentFileName] = useState('');
  const [previewDoc, setPreviewDoc] = useState<{ title: string; fileUrl: string; type: string; ownerId?: string } | null>(null);

  const [reviews, setReviews] = useState<Review[]>([]);
  const [activeReviewFlow, setActiveReviewFlow] = useState<{ job: Job, revieweeName: string, revieweeId: string } | null>(null);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

  const [localReqs, setLocalReqs] = useState<LocalRequirement[]>([]);
  const [isResearchingReqs, setIsResearchingReqs] = useState(false);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const { user, loading } = useFirebase();

  // Create a consistently identified currentUser wrapper against the toggleable roles
  const baseProfile = role === UserRole.WORKER ? workerProfile : employerProfile;
  const currentUser: UserProfile = {
    ...baseProfile,
    id: user?.uid || baseProfile.id,
    name: user?.displayName || user?.email || baseProfile.name,
    avatar: user?.photoURL || baseProfile.avatar,
    verifications: [VerificationType.EMAIL]
  };

  useEffect(() => {
    // Sync jobs
    const unsubJobs = onSnapshot(collection(db, 'arborlink_jobs'), (snapshot) => {
      if (!snapshot.empty) {
        const raw = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Job));
        // defensive: drop content-duplicate docs so listings never render 2x/3x
        const seen = new Set<string>();
        setJobs(raw.filter(j => {
          const key = j.companyId + '|' + j.title + '|' + (j.location || '');
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }));
      } else {
        // No sample seeding — the board stays empty until real jobs are posted
        setJobs([]);
      }
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'arborlink_jobs'));

    // Sync forum posts
    const unsubForum = onSnapshot(collection(db, 'arborlink_forum'), (snapshot) => {
      if (!snapshot.empty) {
        // Sort by timestamp desc
        const posts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ForumPost));
        posts.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setForumPosts(posts);
      } else {
        // No sample seeding — the forum stays empty until real posts are made
        setForumPosts([]);
      }
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'arborlink_forum'));
    
    // Sync conversations
    const unsubConvos = onSnapshot(collection(db, 'arborlink_conversations'), (snapshot) => {
      if (!snapshot.empty) {
        setConversations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Conversation)));
      } else {
        setConversations([]);
      }
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'arborlink_conversations'));

    return () => {
      unsubJobs();
      unsubForum();
      unsubConvos();
    };
  }, []);

  useEffect(() => {
    if (currentUser.location) {
      setIsResearchingReqs(true);
      researchLocalRequirements(currentUser.location).then(reqs => {
        setLocalReqs(reqs);
        setIsResearchingReqs(false);
      });
    }
  }, [currentUser.location, role]);

  const handleApply = async (jobId: string) => {
    if (appliedJobIds.includes(jobId)) return;
    setAppliedJobIds(prev => [...prev, jobId]);
    
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      const newConv: Omit<Conversation, "id"> = {
        jobId: job.id,
        workerId: currentUser.id,
        employerId: job.companyId,
        messages: [
          {
            id: 'init_msg',
            senderId: currentUser.id,
            text: `Hi, I've just applied for the "${job.title}" position. I'm excited to discuss how my skills as a ${workerProfile.skills[0]} can help!`,
            timestamp: new Date().toISOString()
          }
        ],
        lastMessageAt: new Date().toISOString()
      };
      
      try {
        await addDoc(collection(db, 'arborlink_conversations'), newConv);
      } catch(e) { console.error(e) }
      
      setIsAnalyzing(true);
      const analysis = await analyzeJobMatch(job, workerProfile);
      setActiveAnalysis({ jobId, result: analysis });
      setIsAnalyzing(false);
    }
  };

  const handleViewDetails = (jobId: string) => {
    const job = jobs.find(j => j.id === jobId);
    if (job) setSelectedJob(job);
  };

  const handleLikePost = async (postId: string) => {
    const post = forumPosts.find(p => p.id === postId);
    if (!post) return;
    const isLiked = post.likes.includes(currentUser.id);
    try {
      await updateDoc(doc(db, 'arborlink_forum', postId), {
        likes: isLiked ? arrayRemove(currentUser.id) : arrayUnion(currentUser.id)
      });
    } catch(e) { console.error(e) }
  };

  const handleCommentPost = async (postId: string, text: string) => {
    const newComment: ForumComment = {
      id: `c_${Date.now()}`,
      authorId: currentUser.id,
      authorName: currentUser.name,
      authorAvatar: currentUser.avatar,
      text,
      timestamp: new Date().toISOString()
    };
    try {
      await updateDoc(doc(db, 'arborlink_forum', postId), {
        comments: arrayUnion(newComment)
      });
    } catch (e) { console.error(e) }
  };

  const handleFollowUser = (authorId: string) => {
    if (role === UserRole.WORKER) {
      setWorkerProfile(prev => ({ ...prev, following: [...(prev.following || []), authorId] }));
    } else {
      setEmployerProfile(prev => ({ ...prev, following: [...(prev.following || []), authorId] }));
    }
  };

  const handleCreateForumPost = async (title: string, content: string, category: ForumPost['category'], imageUrl?: string) => {
    const newPost: Omit<ForumPost, "id"> = {
      authorId: currentUser.id,
      authorName: currentUser.name,
      authorAvatar: currentUser.avatar,
      title,
      content,
      category,
      imageUrl,
      likes: [],
      comments: [],
      timestamp: new Date().toISOString()
    };
    try {
      await addDoc(collection(db, 'arborlink_forum'), newPost);
    } catch(e) { console.error(e) }
  };

  const handleSyncToGoogleCalendar = (job: Job) => {
    const start = job.date.replace(/-/g, '') + 'T090000Z';
    const end = job.date.replace(/-/g, '') + 'T170000Z';
    const title = encodeURIComponent(`ArborLink: ${job.title}`);
    const details = encodeURIComponent(job.description);
    const location = encodeURIComponent(job.location);
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}`;
    window.open(url, '_blank');
  };

  const handleSendMessage = async (text: string, type: Message['type'] = 'TEXT', proposalData?: Message['proposalData']) => {
    if (!activeConversationId) return;
    const newMessage: Message = {
      id: `msg_${Date.now()}`,
      senderId: currentUser.id,
      text,
      timestamp: new Date().toISOString(),
      type,
      proposalData
    };
    try {
      await updateDoc(doc(db, 'arborlink_conversations', activeConversationId), {
        messages: arrayUnion(newMessage),
        lastMessageAt: newMessage.timestamp
      });
    } catch(e) { console.error(e) }
  };

  const handleCreateOrUpdateJob = async (jobData: Omit<Job, 'id' | 'companyId' | 'companyName' | 'applicants'>) => {
    if (editingJob) {
      try {
        await updateDoc(doc(db, 'arborlink_jobs', editingJob.id), jobData);
      } catch(e) { console.error(e) }
    } else {
      const newJob: Omit<Job, "id"> = {
        ...jobData,
        companyId: currentUser.id,
        companyName: currentUser.name,
        applicants: []
      };
      try {
        await addDoc(collection(db, 'arborlink_jobs'), newJob);
      } catch(e) { console.error(e) }
    }
    setIsJobFormOpen(false);
    setEditingJob(undefined);
    if (!editingJob) setView('manage-jobs');
  };

  const handleUpdateStatus = async (jobId: string, status: JobStatus) => {
    const job = jobs.find(j => j.id === jobId);
    try {
      await updateDoc(doc(db, 'arborlink_jobs', jobId), { status });
    } catch(e) { console.error(e) }
    
    if (status === JobStatus.COMPLETED && job) {
      if (role === UserRole.EMPLOYER) {
        setActiveReviewFlow({ job, revieweeName: workerProfile.name, revieweeId: currentUser.id });
      } else {
        setActiveReviewFlow({ job, revieweeName: job.companyName, revieweeId: job.companyId });
      }
    }
  };

  const handleReviewSubmit = (rating: number, comment: string) => {
    if (!activeReviewFlow) return;
    const newReview: Review = {
      id: `rev_${Date.now()}`,
      jobId: activeReviewFlow.job.id,
      reviewerId: currentUser.id,
      revieweeId: activeReviewFlow.revieweeId,
      rating,
      comment,
      timestamp: new Date().toISOString()
    };
    setReviews(prev => [...prev, newReview]);
    setActiveReviewFlow(null);
  };

  const handleLicenseUpload = (licenseData: Omit<License, 'id' | 'status'>) => {
    const newLicense: License = { ...licenseData, id: `lic_${Date.now()}`, status: 'PENDING' };
    if (role === UserRole.EMPLOYER) {
      setEmployerProfile(prev => ({ ...prev, licenses: [...prev.licenses, newLicense] }));
    } else {
      setWorkerProfile(prev => ({ ...prev, licenses: [...prev.licenses, newLicense] }));
    }
  };

  const handlePortfolioUpload = (projectData: Omit<PortfolioProject, 'id'>) => {
    const newProject: PortfolioProject = { ...projectData, id: `port_${Date.now()}` };
    setWorkerProfile(prev => ({ ...prev, portfolio: [...(prev.portfolio || []), newProject] }));
  };

  const handleVouchSubmit = (skill: SkillType, comment: string) => {
    const newVouch: Vouch = {
      id: `vouch_${Date.now()}`,
      skill,
      vouchedById: employerProfile.id,
      vouchedByName: employerProfile.name,
      comment,
      timestamp: new Date().toISOString()
    };
    setWorkerProfile(prev => ({ ...prev, vouches: [...(prev.vouches || []), newVouch] }));
    setIsVouchModalOpen(false);
  };

  const handleDocumentUploadSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentTitle || !documentFile) return;

    const newDoc = {
      id: `doc_${Date.now()}`,
      title: documentTitle,
      type: documentType,
      fileName: documentFileName || 'attachment.pdf',
      fileUrl: documentFile,
      uploadDate: new Date().toISOString(),
      ownerId: currentUser.id
    };

    setWorkerProfile(prev => ({
      ...prev,
      documents: [...(prev.documents || []), newDoc]
    }));

    // Reset fields
    setDocumentTitle('');
    setDocumentType('certification');
    setDocumentFile(null);
    setDocumentFileName('');
    setIsDocumentVaultOpen(false);
  };

  const handleDocumentDelete = (docId: string) => {
    setWorkerProfile(prev => ({
      ...prev,
      documents: (prev.documents || []).filter(d => d.id !== docId)
    }));
  };

  const handleEditJob = (job: Job) => {
    setEditingJob(job);
    setIsJobFormOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'avatar' | 'banner') => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        if (role === UserRole.EMPLOYER) setEmployerProfile(prev => ({ ...prev, [type]: base64 }));
        else setWorkerProfile(prev => ({ ...prev, [type]: base64 }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAssignToJob = (memberId: string, jobId: string) => {
    if (role !== UserRole.EMPLOYER) return;
    setEmployerProfile(prev => ({
      ...prev,
      team: prev.team?.map(m => m.id === memberId ? { ...m, assignedJobIds: [...m.assignedJobIds, jobId] } : m)
    }));
  };

  const activeConversation = useMemo(() => conversations.find(c => c.id === activeConversationId), [conversations, activeConversationId]);
  const employerJobs = useMemo(() => jobs.filter(j => j.companyId === employerProfile.id), [jobs, employerProfile.id]);

  const stats = useMemo(() => {
    const open = jobs.filter(j => j.status === JobStatus.OPEN).length;
    const pendingApps = conversations.length;
    const filledJobs = jobs.filter(j => j.status === JobStatus.FILLED).length;
    return {
      openJobs: open,
      myApplications: appliedJobIds.length,
      unreadMessages: conversations.length,
      pendingApplications: pendingApps,
      upcomingJobsCount: filledJobs
    };
  }, [jobs, appliedJobIds, conversations]);

  const averageRating = (id: string) => {
    const userReviews = reviews.filter(r => r.revieweeId === id);
    if (userReviews.length === 0) {
      if (id === employerProfile.id) return employerProfile.rating;
      if (id === workerProfile.id) return workerProfile.rating;
      return 4.8;
    }
    const sum = userReviews.reduce((acc, r) => acc + r.rating, 0);
    return parseFloat((sum / userReviews.length).toFixed(1));
  };

  const interviewsFromProposals = useMemo(() => {
    const allProposals: Array<{ id: string, title: string, date: string }> = [];
    conversations.forEach(conv => {
      const job = jobs.find(j => j.id === conv.jobId);
      conv.messages.forEach(msg => {
        if (msg.type === 'INTERVIEW_PROPOSAL' && msg.proposalData) {
          allProposals.push({ id: msg.id, title: `Interview: ${job?.title || 'Unknown Job'}`, date: msg.proposalData.time });
        }
      });
    });
    return allProposals;
  }, [conversations, jobs]);

  return (
    <div className="h-full bg-slate-50 flex flex-col md:flex-row font-inter rounded-2xl overflow-hidden border border-stone-200">
      {/* Sidebar */}
      <aside className="w-full md:w-64 bg-white border-r border-slate-200 flex-shrink-0 flex flex-col z-10 md:z-auto">
        <div className="hidden md:flex items-center space-x-3 p-6 mb-4">
          <div className="bg-emerald-600 p-1.5 rounded-xl shadow-lg shadow-emerald-100">
             <img src={ASSETS.LOGO_MONKEY} className="w-8 h-8 object-contain brightness-0 invert" alt="Giddy Gibbon" />
          </div>
          <span className="text-2xl font-black text-slate-800 tracking-tight italic">ArborLink</span>
        </div>

        <nav className="flex-1 flex md:flex-col justify-around md:justify-start px-2 md:px-4 md:space-y-1">
          <button onClick={() => setView('dashboard')} className={`flex items-center p-3 md:px-4 md:py-3 rounded-xl transition-all ${view === 'dashboard' ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-500 hover:bg-slate-50 font-semibold'}`}>
            <LayoutDashboard className="w-6 h-6 md:mr-3" /><span className="hidden md:block">Dashboard</span>
          </button>
          
          <button onClick={() => setView('community')} className={`flex items-center p-3 md:px-4 md:py-3 rounded-xl transition-all ${view === 'community' ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-500 hover:bg-slate-50 font-semibold'}`}>
            <Globe className="w-6 h-6 md:mr-3" /><span className="hidden md:block">Community</span>
          </button>

          {role === UserRole.EMPLOYER && (
            <>
              <button onClick={() => setView('manage-jobs')} className={`flex items-center p-3 md:px-4 md:py-3 rounded-xl transition-all ${view === 'manage-jobs' ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-500 hover:bg-slate-50 font-semibold'}`}>
                <Briefcase className="w-6 h-6 md:mr-3" /><span className="hidden md:block">My Jobs</span>
              </button>
              <button onClick={() => setView('team')} className={`flex items-center p-3 md:px-4 md:py-3 rounded-xl transition-all ${view === 'team' ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-500 hover:bg-slate-50 font-semibold'}`}>
                <Users className="w-6 h-6 md:mr-3" /><span className="hidden md:block">Team Mgmt</span>
              </button>
            </>
          )}

          {role === UserRole.WORKER && (
            <button onClick={() => setView('jobs')} className={`flex items-center p-3 md:px-4 md:py-3 rounded-xl transition-all ${view === 'jobs' ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-500 hover:bg-slate-50 font-semibold'}`}>
              <Search className="w-6 h-6 md:mr-3" /><span className="hidden md:block">Find Work</span>
            </button>
          )}

          <button onClick={() => setView('messages')} className={`flex items-center p-3 md:px-4 md:py-3 rounded-xl transition-all relative ${view === 'messages' ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-500 hover:bg-slate-50 font-semibold'}`}>
            <MessageSquare className="w-6 h-6 md:mr-3" /><span className="hidden md:block">Messages</span>
            {stats.unreadMessages > 0 && <span className="absolute top-2 right-2 md:static md:ml-auto bg-emerald-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center border-2 border-white">{stats.unreadMessages}</span>}
          </button>
          
          <button onClick={() => setView('calendar')} className={`flex items-center p-3 md:px-4 md:py-3 rounded-xl transition-all ${view === 'calendar' ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-500 hover:bg-slate-50 font-semibold'}`}>
            <Calendar className="w-6 h-6 md:mr-3" /><span className="hidden md:block">Calendar</span>
          </button>
        </nav>

        <div className="hidden md:block p-4 border-t border-slate-100">
          <div className="flex items-center p-2 rounded-xl hover:bg-slate-50 cursor-pointer">
            <img src={currentUser.avatar} alt="Avatar" className="w-10 h-10 rounded-full border-2 border-emerald-500 bg-white" />
            <div className="ml-3">
              <p className="text-sm font-bold text-slate-800 truncate w-24">{currentUser.name}</p>
              <p className="text-xs text-slate-500 uppercase tracking-tighter font-bold">{role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pb-24 md:pb-0">
        <header className="bg-white border-b border-slate-200 p-4 md:px-8 md:py-6 flex justify-between items-center sticky top-0 z-10">
          <div className="flex items-center gap-3">
             <img src={ASSETS.LOGO_MONKEY} className="w-10 h-10 hidden md:block rounded-lg object-contain" alt="Monkey Logo" />
             <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight">
               {view === 'dashboard' && 'Crew Hub Dashboard'}
               {view === 'jobs' && 'Available Job Opportunities'}
               {view === 'manage-jobs' && 'Job Postings Manager'}
               {view === 'calendar' && 'Schedule Management'}
               {view === 'profile' && 'Professional Profile'}
               {view === 'messages' && 'Messaging Hub'}
               {view === 'community' && 'Community Crew Room'}
               {view === 'team' && 'Team Management Hub'}
             </h1>
          </div>
          <button onClick={() => { setRole(role === UserRole.WORKER ? UserRole.EMPLOYER : UserRole.WORKER); setView('dashboard'); }} className="text-xs font-black px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-100 active:scale-95">
            Switch to {role === UserRole.WORKER ? 'Employer' : 'Worker'}
          </button>
        </header>

        <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8">
          {view === 'community' && (
            <Forum 
              posts={forumPosts}
              currentUser={currentUser}
              onLike={handleLikePost}
              onComment={handleCommentPost}
              onFollow={handleFollowUser}
              onPost={handleCreateForumPost}
            />
          )}

          {view === 'team' && role === UserRole.EMPLOYER && (
            <div className="space-y-8 animate-in fade-in duration-500">
               <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-black text-slate-800 tracking-tight">Technical Crew Roster</h2>
                  <button className="bg-slate-900 text-white px-6 py-3 rounded-2xl font-black text-sm hover:bg-slate-800 transition-all flex items-center gap-2">
                    <UserPlus className="w-4 h-4" /> Add Team Member
                  </button>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                 {employerProfile.team?.map(member => (
                   <div key={member.id} className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm p-8 flex flex-col hover:border-emerald-300 transition-all group">
                     <div className="flex items-center gap-4 mb-6">
                       <img src={member.avatar} className="w-16 h-16 rounded-[1.5rem] border-2 border-slate-50 object-cover" alt={member.name} />
                       <div>
                         <h3 className="font-black text-slate-800 text-lg leading-tight">{member.name}</h3>
                         <div className="flex gap-1 mt-1">
                           {member.skills.slice(0, 2).map(s => <span key={s} className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{s}</span>)}
                         </div>
                       </div>
                     </div>

                     <div className="space-y-6">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-2">
                             <CalendarDays className="w-3.5 h-3.5" /> Google Calendar Sync
                          </p>
                          <div className="grid grid-cols-4 gap-2">
                            {member.availability.map((ev, i) => (
                              <div key={i} className={`p-2 rounded-xl text-center border ${ev.status === 'BUSY' ? 'bg-amber-50 border-amber-100' : 'bg-emerald-50 border-emerald-100'}`}>
                                <p className="text-[10px] font-black text-slate-800">{ev.date.split('-')[2]}</p>
                                <p className={`text-[8px] font-black uppercase tracking-tighter ${ev.status === 'BUSY' ? 'text-amber-600' : 'text-emerald-600'}`}>{ev.status}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                           <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-2">
                              <HardHat className="w-3.5 h-3.5" /> Current Deployments
                           </p>
                           <div className="space-y-2">
                             {member.assignedJobIds.map(jid => {
                               const job = jobs.find(j => j.id === jid);
                               return (
                                 <div key={jid} className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between">
                                    <span className="text-xs font-bold text-slate-700 truncate">{job?.title || 'Unknown Project'}</span>
                                    <span className="text-[10px] font-black text-emerald-600 uppercase">Tracked</span>
                                 </div>
                               );
                             })}
                           </div>
                        </div>
                     </div>

                     <div className="mt-auto pt-8 flex gap-2">
                       <button className="flex-1 py-3 bg-slate-100 text-slate-600 font-black text-xs rounded-xl hover:bg-slate-200 transition-all">View Full Bio</button>
                       <button className="flex-1 py-3 bg-emerald-600 text-white font-black text-xs rounded-xl hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-100">
                         <Briefcase className="w-3.5 h-3.5" /> Assign Job
                       </button>
                     </div>
                   </div>
                 ))}
               </div>
            </div>
          )}

          {view === 'dashboard' && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
               {/* Integration Bar */}
               <div className="flex flex-wrap gap-3 bg-white p-3 rounded-2xl border border-slate-200 shadow-sm overflow-x-auto no-scrollbar">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-[10px] font-black uppercase tracking-widest border border-blue-100">
                    <CalendarDays className="w-3.5 h-3.5" /> Google Calendar Synced
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-[10px] font-black uppercase tracking-widest border border-emerald-100">
                    <MapPin className="w-3.5 h-3.5" /> Google Maps Grounding Active
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-[10px] font-black uppercase tracking-widest border border-slate-800">
                    <Bot className="w-3.5 h-3.5" /> AI Ops Assistant Live
                  </div>
               </div>

               {/* Branding / Mascot Hero */}
               <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2 bg-emerald-600 rounded-[3rem] p-10 text-white relative overflow-hidden shadow-2xl flex items-center">
                    <div className="relative z-10">
                      <h2 className="text-4xl font-black mb-4 italic tracking-tight">ArborLink x GreenTree</h2>
                      <p className="text-emerald-100 text-lg font-medium leading-relaxed max-w-md">
                        Managing technical rigging and crew safety at scale. Powerful tools for the modern arborist.
                      </p>
                      <button onClick={() => setView('community')} className="mt-8 px-8 py-4 bg-white text-emerald-700 font-black rounded-2xl hover:bg-emerald-50 transition-all flex items-center gap-2 shadow-xl shadow-emerald-900/40">
                        <Globe className="w-5 h-5" /> Visit the Crew Room
                      </button>
                    </div>
                    <div className="absolute top-1/2 -translate-y-1/2 right-10 opacity-20 hidden lg:block">
                       <img src={ASSETS.LOGO_MONKEY} className="w-64 h-64 object-contain brightness-0 invert" alt="Gibbon" />
                    </div>
                  </div>
                  <div className="bg-white p-8 rounded-[3rem] border border-slate-200 shadow-sm flex flex-col items-center justify-center text-center">
                     <img src={ASSETS.LOGO_MONKEY} className="w-24 h-24 object-contain mb-4 animate-bounce duration-[2000ms]" alt="Giddy" />
                     <h3 className="text-xl font-black text-slate-800 italic">Giddy Gibbon Says:</h3>
                     <p className="text-slate-500 font-medium mt-2 italic">"Assigning the right climber to the right lead makes all the difference."</p>
                  </div>
               </div>

               {role === UserRole.EMPLOYER && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                     <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
                        <div className="flex justify-between items-center mb-8">
                           <h3 className="font-black text-slate-800 text-lg">Team Quick Glance</h3>
                           <button onClick={() => setView('team')} className="text-xs font-black text-emerald-600 hover:underline">Manage All Members</button>
                        </div>
                        <div className="space-y-4">
                           {employerProfile.team?.map(m => (
                             <div key={m.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                                <div className="flex items-center gap-3">
                                   <img src={m.avatar} className="w-10 h-10 rounded-xl object-cover" alt={m.name} />
                                   <div>
                                      <p className="text-sm font-bold text-slate-800">{m.name}</p>
                                      <p className="text-[10px] font-black text-slate-400 uppercase">{m.skills[0]}</p>
                                   </div>
                                </div>
                                <div className="flex items-center gap-2">
                                   <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                   <span className="text-[10px] font-black text-emerald-600 uppercase">Ready</span>
                                </div>
                             </div>
                           ))}
                        </div>
                     </div>

                     <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden">
                        <div className="relative z-10 h-full flex flex-col justify-center">
                           <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400 mb-2">Operational Tip</p>
                           <h3 className="text-2xl font-black mb-4 leading-tight">Need specialized equipment for a member?</h3>
                           <p className="text-slate-400 text-sm font-medium mb-8">Ask the AI Assistant to find local rental deals for your crew based on their upcoming job locations.</p>
                           <button className="w-fit px-8 py-3 bg-emerald-600 text-white font-black rounded-xl hover:bg-emerald-500 transition-all flex items-center gap-2">
                             <Bot className="w-5 h-5" /> Ask AI Ops
                           </button>
                        </div>
                        <Bot className="absolute -right-8 -bottom-8 w-48 h-48 opacity-10" />
                     </div>
                  </div>
               )}

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
                  <div className="bg-blue-50 p-3 rounded-2xl w-fit mb-4 text-blue-600"><UserPlus className="w-6 h-6" /></div>
                  <p className="text-sm font-bold text-slate-500 uppercase tracking-wider">{role === UserRole.EMPLOYER ? 'Applicants' : 'Gigs'}</p>
                  <p className="text-3xl font-black text-slate-800">{role === UserRole.EMPLOYER ? stats.pendingApplications : stats.openJobs}</p>
                </div>
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
                  <div className="bg-amber-50 p-3 rounded-2xl w-fit mb-4 text-amber-600"><Briefcase className="w-6 h-6" /></div>
                  <p className="text-sm font-bold text-slate-500 uppercase tracking-wider">Upcoming</p>
                  <p className="text-3xl font-black text-slate-800">{stats.upcomingJobsCount}</p>
                </div>
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
                  <div className="bg-emerald-50 p-3 rounded-2xl w-fit mb-4 text-emerald-600"><CheckCircle2 className="w-6 h-6" /></div>
                  <p className="text-sm font-bold text-slate-500 uppercase tracking-wider">Completed</p>
                  <p className="text-3xl font-black text-slate-800">{jobs.filter(j => j.status === JobStatus.COMPLETED).length}</p>
                </div>
                <div className="bg-emerald-600 p-6 rounded-3xl shadow-xl text-white relative overflow-hidden">
                  <div className="relative z-10">
                    <div className="bg-white/20 p-3 rounded-2xl w-fit mb-4"><Star className="w-6 h-6" /></div>
                    <p className="text-sm font-bold text-emerald-100 uppercase tracking-wider">Rating</p>
                    <div className="flex items-baseline gap-2"><p className="text-3xl font-black">{averageRating(currentUser.id)}</p><Star className="w-5 h-5 fill-current text-emerald-200" /></div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center px-2">
                <h2 className="text-2xl font-black text-slate-800">{role === UserRole.EMPLOYER ? 'Active Recruitments' : 'Available Gig List'}</h2>
                <button onClick={() => setView('jobs')} className="text-sm font-black text-emerald-600 hover:underline">View All</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {jobs.filter(j => j.status === JobStatus.OPEN).slice(0, 3).map(job => (
                  <JobCard 
                    key={job.id} 
                    job={job} 
                    userRole={role} 
                    onApply={handleApply} 
                    onViewDetails={handleViewDetails}
                    isApplied={appliedJobIds.includes(job.id)} 
                    employerRating={averageRating(job.companyId)} 
                    companyLogo={job.companyId === employerProfile.id ? employerProfile.avatar : undefined} 
                  />
                ))}
              </div>
            </div>
          )}

          {view === 'calendar' && (
            <div className="max-w-5xl mx-auto space-y-6 animate-in zoom-in-95 duration-300">
              <JobCalendar jobs={jobs} interviews={interviewsFromProposals} onJobClick={(job) => { if (role === UserRole.EMPLOYER && job.companyId === employerProfile.id) { handleEditJob(job); } else { handleViewDetails(job.id); } }} />
            </div>
          )}

          {view === 'jobs' && (
            <div className="space-y-8 animate-in fade-in duration-300">
              <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col md:flex-row gap-6 items-center">
                <div className="relative flex-1 w-full"><Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" /><input type="text" placeholder="Search removals..." className="w-full pl-12 pr-4 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-emerald-500 transition-all outline-none font-medium" /></div>
                <button className="bg-slate-800 text-white px-10 py-4 rounded-2xl font-black hover:bg-slate-700 transition-all active:scale-95">Find Gigs</button>
              </div>
              {jobs.length === 0 && (
                <div className="bg-white p-10 rounded-[2rem] border border-slate-200 text-center">
                  <span className="text-4xl">📋</span>
                  <p className="font-black text-slate-700 mt-3">No open jobs yet</p>
                  <p className="text-sm text-slate-400 mt-1">Post a real opening and it appears here instantly.</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {jobs.map(job => (
                  <JobCard 
                    key={job.id} 
                    job={job} 
                    userRole={role} 
                    onApply={handleApply} 
                    onViewDetails={handleViewDetails}
                    isApplied={appliedJobIds.includes(job.id)} 
                    employerRating={averageRating(job.companyId)} 
                    companyLogo={job.companyId === employerProfile.id ? employerProfile.avatar : undefined} 
                  />
                ))}
              </div>
            </div>
          )}

          {view === 'profile' && (
            <div className="bg-white rounded-[3rem] shadow-sm border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-500 relative p-12">
               <div className="flex justify-between items-start mb-12">
                  <div>
                    <h2 className="text-4xl font-black text-slate-800 mb-2">{currentUser.name}</h2>
                    <p className="text-xs font-black text-emerald-600 uppercase tracking-widest flex items-center gap-2">
                       <ShieldCheck className="w-4 h-4" /> Fully Verified ArborLink Member
                    </p>
                  </div>
                  <button onClick={() => setIsEditingProfile(!isEditingProfile)} className="px-8 py-3 bg-slate-900 text-white font-black rounded-xl hover:bg-slate-800 transition-all">Edit Profile</button>
               </div>
               
               <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                 <div className="lg:col-span-2 space-y-8">
                    <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100">
                      <h3 className="font-black text-slate-800 mb-4 flex items-center gap-2"><Briefcase className="w-5 h-5 text-emerald-600" /> About</h3>
                      <p className="font-medium text-slate-600 leading-relaxed">{currentUser.bio}</p>
                    </div>

                    <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100">
                       <h3 className="font-black text-slate-800 mb-4 flex items-center gap-2"><Award className="w-5 h-5 text-emerald-600" /> Verified Skills</h3>
                       <div className="flex flex-wrap gap-2">
                          {currentUser.skills.map(s => <span key={s} className="px-4 py-2 bg-white rounded-xl border border-slate-200 text-xs font-black shadow-sm">{s}</span>)}
                       </div>
                    </div>

                    <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100">
                        <div className="flex justify-between items-center mb-6">
                           <h3 className="font-black text-slate-800 flex items-center gap-2">
                             <FileText className="w-5 h-5 text-emerald-600" /> Document Vault
                           </h3>
                           <span className="text-[10px] bg-slate-200 text-slate-600 font-black uppercase px-2.5 py-1 rounded-full tracking-widest flex items-center gap-1">
                             <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> AES-256 cloud secure
                           </span>
                        </div>
                        
                        <div className="space-y-3">
                          {(!currentUser.documents || currentUser.documents.length === 0) ? (
                            <div className="text-center py-6 px-4 bg-white rounded-2xl border border-dashed border-slate-200">
                              <Fingerprint className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                              <p className="text-xs font-bold text-slate-500">No documents uploaded yet</p>
                              <p className="text-[10px] text-slate-400 mt-0.5">Upload safety certs, EHAP, or licenses for instant validation.</p>
                            </div>
                          ) : (
                            currentUser.documents.map(doc => (
                              <div key={doc.id} className="flex justify-between items-center p-4 bg-white rounded-xl border border-slate-200 hover:shadow-sm transition-all group">
                                <div className="flex items-center gap-3">
                                  <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                                    <FileText className="w-4 h-4" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-black text-slate-700">{doc.title}</p>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">{doc.type} • Uploaded {new Date(doc.uploadDate).toLocaleDateString()}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button 
                                    onClick={() => setPreviewDoc({ title: doc.title, fileUrl: doc.fileUrl, type: doc.type, ownerId: doc.ownerId })} 
                                    className="px-3 py-1.5 bg-slate-100 text-slate-600 font-bold text-xs rounded-lg hover:bg-slate-200 transition-all"
                                  >
                                    View
                                  </button>
                                  <button 
                                    onClick={() => handleDocumentDelete(doc.id)}
                                    className="p-1 text-slate-450 hover:text-red-500 transition-all"
                                    title="Delete Document"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                          <button onClick={() => setIsDocumentVaultOpen(true)} className="w-full py-3 bg-emerald-100 text-emerald-700 font-black text-xs rounded-xl hover:bg-emerald-200 transition-all mt-3">
                            Upload New Document
                          </button>
                        </div>
                     </div>
                 </div>

                 <div className="space-y-6">
                    <div className="bg-emerald-600 rounded-[2.5rem] p-8 text-white text-center flex flex-col items-center">
                       <p className="text-[10px] font-black uppercase tracking-widest text-emerald-200 mb-6">ArborLink Score</p>
                       <div className="text-7xl font-black mb-4">4.9</div>
                       <div className="flex gap-1 text-emerald-300 mb-6">
                          {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}
                       </div>
                       <p className="text-sm font-bold text-emerald-100">Based on 12 verified project completions</p>
                    </div>
                     {role === UserRole.EMPLOYER && (
                        <div className="bg-white rounded-[2.5rem] p-8 border border-slate-200 shadow-sm mt-8">
                           <h3 className="font-black text-slate-800 text-lg mb-6 flex items-center gap-2">
                             <Share2 className="w-5 h-5 text-emerald-600" /> Recruitment QR Code
                           </h3>
                           <div className="flex items-center gap-6">
                              <QRCodeCanvas value="https://greentreearboriculture.com/#/arborlink" size={128} />
                              <div>
                                 <p className="text-sm font-bold text-slate-700 mb-2">Generate flyer QR code</p>
                                 <p className="text-xs text-slate-500 mb-4">Print this QR code on your recruitment flyers to link directly to your open positions.</p>
                                 <button className="px-4 py-2 bg-emerald-600 text-white font-black text-xs rounded-lg hover:bg-emerald-700 transition-all">Print Flyer</button>
                              </div>
                           </div>
                        </div>
                     )}
                 </div>
               </div>
            </div>
          )}

          {view === 'messages' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[75vh]">
              <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                 <div className="p-6 border-b border-slate-100 bg-slate-50/50 font-black">Active Conversations</div>
                 <div className="flex-1 overflow-y-auto">
                    {conversations.map(conv => {
                      const otherUser = role === UserRole.WORKER ? { name: employerProfile.name, avatar: employerProfile.avatar } : { name: workerProfile.name, avatar: workerProfile.avatar };
                      return (
                        <button 
                          key={conv.id} 
                          onClick={() => setActiveConversationId(conv.id)}
                          className={`w-full p-6 text-left border-b border-slate-50 flex items-center gap-4 hover:bg-slate-50 transition-all ${activeConversationId === conv.id ? 'bg-emerald-50' : ''}`}
                        >
                           <img src={otherUser.avatar} className="w-12 h-12 rounded-xl object-cover" alt="User" />
                           <div className="min-w-0">
                              <p className="font-black text-slate-800 text-sm truncate">{otherUser.name}</p>
                              <p className="text-[10px] font-bold text-slate-400 truncate">{conv.messages[conv.messages.length - 1]?.text}</p>
                           </div>
                        </button>
                      );
                    })}
                 </div>
              </div>
              <div className="md:col-span-2">
                 {activeConversation ? (
                   <ChatWindow 
                     conversation={activeConversation} 
                     currentUser={currentUser} 
                     otherUser={role === UserRole.WORKER ? { name: employerProfile.name, avatar: employerProfile.avatar } : { name: workerProfile.name, avatar: workerProfile.avatar }} 
                     onSendMessage={handleSendMessage} 
                     onBack={() => setActiveConversationId(null)} 
                   />
                 ) : (
                   <div className="h-full bg-white rounded-[2rem] border-2 border-slate-100 border-dashed flex flex-col items-center justify-center">
                     <img src={ASSETS.LOGO_MONKEY} className="w-24 h-24 opacity-10 mb-6" alt="Monkey" />
                     <h3 className="text-2xl font-black text-slate-300">Select a Crew Member</h3>
                   </div>
                 )}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer branding */}
        <footer className="p-12 border-t border-slate-200 text-center space-y-4">
           <img src={ASSETS.LOGO_MONKEY} className="w-12 h-12 mx-auto grayscale opacity-50" alt="Footer Logo" />
           <p className="text-sm font-black text-slate-400 italic">ArborLink is a service provided and powered by GreenTree Arboriculture.</p>
           <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.3em]">Precision • Safety • Operational Transparency</p>
        </footer>
      </main>

      <AiAssistant jobs={jobs} workers={[workerProfile]} globalRequirements={localReqs} />

      {/* Overlays */}
      {isJobFormOpen && <JobForm onSubmit={handleCreateOrUpdateJob} onCancel={() => setIsJobFormOpen(false)} initialData={editingJob} />}

      {/* Document Vault Upload Modal */}
      {isDocumentVaultOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[210] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div>
                <h2 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
                  <Upload className="w-6 h-6 text-emerald-600" /> Upload Credentials
                </h2>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">AES-256 secure verification vault</p>
              </div>
              <button 
                onClick={() => {
                  setDocumentTitle('');
                  setDocumentType('certification');
                  setDocumentFile(null);
                  setDocumentFileName('');
                  setIsDocumentVaultOpen(false);
                }} 
                className="p-2.5 hover:bg-slate-200 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-slate-450" />
              </button>
            </div>

            <form onSubmit={handleDocumentUploadSubmit} className="p-8 space-y-6 overflow-y-auto">
              <div className="space-y-1.5">
                <label className="block text-xs font-black text-slate-500 uppercase tracking-wider">Document Title</label>
                <input 
                  type="text"
                  required
                  placeholder="e.g. ISA Certified Arborist License, EHAP Cert"
                  value={documentTitle}
                  onChange={e => setDocumentTitle(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm font-semibold text-slate-800"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-black text-slate-500 uppercase tracking-wider">Credential Type</label>
                <select 
                  value={documentType}
                  onChange={e => setDocumentType(e.target.value as any)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm font-semibold text-slate-800"
                >
                  <option value="certification">Safety Certification</option>
                  <option value="license">State / Local License</option>
                  <option value="other">Other Credential</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-black text-slate-500 uppercase tracking-wider">Document Artifact</label>
                
                {documentFile ? (
                  <div className="p-4 bg-emerald-50/50 border border-emerald-200 rounded-2xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-emerald-100 text-emerald-700 rounded-xl">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-black text-slate-800 truncate max-w-[200px]">{documentFileName || 'Uploaded File'}</p>
                        <p className="text-[10px] text-emerald-600 font-bold">Successfully Staged</p>
                      </div>
                    </div>
                    <button 
                      type="button"
                      onClick={() => { setDocumentFile(null); setDocumentFileName(''); }}
                      className="text-xs text-red-500 font-bold hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div 
                    onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={e => {
                      e.preventDefault();
                      setIsDragging(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) {
                        setDocumentFileName(file.name);
                        const reader = new FileReader();
                        reader.onloadend = () => setDocumentFile(reader.result as string);
                        reader.readAsDataURL(file);
                      }
                    }}
                    className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
                      isDragging 
                        ? 'border-emerald-600 bg-emerald-50/50' 
                        : 'border-slate-300 hover:border-emerald-500 bg-slate-50'
                    }`}
                  >
                    <input 
                      type="file"
                      accept="image/*,.pdf"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setDocumentFileName(file.name);
                          const reader = new FileReader();
                          reader.onloadend = () => setDocumentFile(reader.result as string);
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="hidden"
                      id="arborlink-doc-file"
                    />
                    <label htmlFor="arborlink-doc-file" className="cursor-pointer block">
                      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="text-sm font-black text-slate-700">Drag credential here or <span className="text-emerald-600 hover:underline">browse</span></p>
                      <p className="text-[10px] text-slate-400 font-medium mt-1">Supports images, receipts, or PDF safety slips up to 10MB.</p>
                    </label>
                  </div>
                )}
              </div>

              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <p className="text-[10px] text-slate-500 font-bold leading-normal leading-relaxed uppercase">
                  GreenTree Arboriculture cryptographic authenticity protocols protect all attachments. Credentials synced via ArborLink are instantly available for employer audit checks and platform auto-match validations.
                </p>
              </div>

              <div className="pt-4 flex gap-3">
                <button 
                  type="button"
                  onClick={() => {
                    setDocumentTitle('');
                    setDocumentType('certification');
                    setDocumentFile(null);
                    setDocumentFileName('');
                    setIsDocumentVaultOpen(false);
                  }} 
                  className="flex-1 py-3.5 bg-slate-100 text-slate-500 font-bold rounded-xl hover:bg-slate-200 transition-all text-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={!documentTitle || !documentFile}
                  className="flex-1 py-3.5 bg-emerald-600 text-white font-black rounded-xl hover:bg-emerald-700 transition-all shadow-md shadow-emerald-250 disabled:opacity-50 text-sm uppercase tracking-wider"
                >
                  Verify & Upload
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[210] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div>
                <h2 className="text-xl font-black text-slate-800 truncate max-w-[320px]">{previewDoc.title}</h2>
                <span className="text-[10px] bg-emerald-100 text-emerald-700 font-black uppercase px-2 py-0.5 rounded-md tracking-widest mt-1 inline-block">
                  {previewDoc.type} • Secured and Verified
                </span>
              </div>
              <button onClick={() => setPreviewDoc(null)} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="p-8 flex-1 overflow-y-auto flex flex-col justify-between">
              <div className="bg-slate-100 rounded-2xl border border-slate-200 overflow-hidden flex items-center justify-center p-2 min-h-[300px] mb-6 relative">
                {previewDoc.fileUrl.startsWith('data:image/') ? (
                  <img src={previewDoc.fileUrl} alt={previewDoc.title} className={`max-h-[350px] object-contain rounded-xl ${!(role === UserRole.EMPLOYER || (previewDoc.ownerId && currentUser.id === previewDoc.ownerId)) ? 'blur-md pointer-events-none' : ''}`} />
                ) : (
                  <div className={`text-center p-8 space-y-3 ${!(role === UserRole.EMPLOYER || (previewDoc.ownerId && currentUser.id === previewDoc.ownerId)) ? 'blur-sm pointer-events-none' : ''}`}>
                    <FileText className="w-16 h-16 text-emerald-600 mx-auto" />
                    <p className="text-sm font-black text-slate-700">Digital Document slip</p>
                    <p className="text-xs text-slate-400 font-semibold italic">Cryptographically sealed verification document</p>
                    <a href={previewDoc.fileUrl} download={previewDoc.title} className="px-5 py-2 bg-emerald-600 text-white text-xs font-black rounded-lg mt-4 inline-block hover:bg-emerald-700 transition shadow-md">
                      Download Original Slip
                    </a>
                  </div>
                )}
                {!(role === UserRole.EMPLOYER || (previewDoc.ownerId && currentUser.id === previewDoc.ownerId)) && (
                   <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/10 z-10 transition-all cursor-not-allowed">
                      <div className="bg-white p-5 rounded-3xl shadow-2xl border border-slate-200 flex flex-col items-center max-w-[280px] text-center">
                         <div className="bg-slate-100 p-3 rounded-full mb-3">
                            <Lock className="w-6 h-6 text-slate-400" />
                         </div>
                         <p className="text-sm font-black text-slate-800">View Restricted</p>
                         <p className="text-xs text-slate-500 mt-2 font-medium">Only the document owner and admin-level employer can view high-resolution files.</p>
                      </div>
                   </div>
                )}
              </div>

              <div className="flex bg-slate-50 border border-slate-150 p-4 rounded-xl items-center gap-3">
                <Fingerprint className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="text-[9px] text-slate-400 font-black uppercase tracking-wider">Blockchain Audit Trail Sign</p>
                  <p className="text-[10px] font-mono text-slate-700 select-all font-bold">SHA-256: {Math.random().toString(36).substring(2, 15).toUpperCase()}GT99X</p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50/50 text-right">
              <button onClick={() => setPreviewDoc(null)} className="px-6 py-3 bg-slate-900 text-white font-black rounded-xl hover:bg-slate-800 transition-all text-xs uppercase tracking-wider">
                Dismiss Verification Preview
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Job Details Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[200] flex items-center justify-center p-4">
           <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
              <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                 <div>
                    <h2 className="text-3xl font-black text-slate-800 tracking-tight">{selectedJob.title}</h2>
                    <p className="text-sm font-bold text-emerald-600 mt-1 uppercase tracking-widest">{selectedJob.companyName}</p>
                 </div>
                 <button onClick={() => setSelectedJob(null)} className="p-3 hover:bg-slate-200 rounded-full transition-colors">
                    <X className="w-6 h-6 text-slate-400" />
                 </button>
              </div>

              <div className="p-10 overflow-y-auto space-y-8">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 flex items-center gap-4">
                       <div className="p-3 bg-white rounded-xl text-emerald-600 shadow-sm"><MapPin className="w-5 h-5" /></div>
                       <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Location</p>
                          <p className="font-bold text-slate-800">{selectedJob.location}</p>
                       </div>
                    </div>
                    <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 flex items-center gap-4">
                       <div className="p-3 bg-white rounded-xl text-blue-600 shadow-sm"><DollarSign className="w-5 h-5" /></div>
                       <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Rate</p>
                          <p className="font-bold text-slate-800">{selectedJob.payRate}</p>
                       </div>
                    </div>
                 </div>

                 <div>
                    <h3 className="font-black text-slate-800 mb-3 flex items-center gap-2">
                       <FileText className="w-5 h-5 text-emerald-600" /> Project Description
                    </h3>
                    <p className="font-medium text-slate-600 leading-relaxed text-lg">{selectedJob.description}</p>
                 </div>

                 <div>
                    <h3 className="font-black text-slate-800 mb-4 flex items-center gap-2">
                       <ListChecks className="w-5 h-5 text-amber-600" /> Crew Requirements
                    </h3>
                    <div className="flex flex-wrap gap-2">
                       {selectedJob.requiredSkills.map(s => <span key={s} className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-black">{s}</span>)}
                       {selectedJob.requiredCerts.map(c => <span key={c} className="px-4 py-2 bg-amber-50 text-amber-700 border border-amber-100 rounded-xl text-xs font-black">{c}</span>)}
                    </div>
                 </div>

                 {selectedJob.deadline && (
                    <div className="p-6 bg-red-50 border border-red-100 rounded-[2rem] flex items-center gap-4">
                       <Clock className="w-6 h-6 text-red-500" />
                       <div>
                          <p className="text-[10px] font-black text-red-400 uppercase tracking-widest">Application Deadline</p>
                          <p className="font-black text-red-700">{selectedJob.deadline}</p>
                       </div>
                    </div>
                 )}
              </div>

              <div className="p-8 border-t border-slate-100 bg-slate-50/50 flex gap-4">
                 <button onClick={() => setSelectedJob(null)} className="flex-1 py-4 bg-white border border-slate-200 text-slate-600 font-black rounded-2xl hover:bg-slate-100 transition-all">Close</button>
                 {role === UserRole.WORKER && (
                    <button 
                       disabled={appliedJobIds.includes(selectedJob.id) || selectedJob.status !== 'OPEN'}
                       onClick={() => { handleApply(selectedJob.id); setSelectedJob(null); }}
                       className="flex-[2] py-4 bg-emerald-600 text-white font-black rounded-2xl hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-200 flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
                    >
                       {appliedJobIds.includes(selectedJob.id) ? <CheckCircle2 className="w-5 h-5" /> : <Zap className="w-5 h-5" />}
                       {appliedJobIds.includes(selectedJob.id) ? 'Already Applied' : 'Quick Apply Now'}
                    </button>
                 )}
              </div>
           </div>
        </div>
      )}

      {isAnalyzing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[300] flex items-center justify-center p-4">
          <div className="bg-white p-12 rounded-[3rem] shadow-2xl max-w-sm w-full text-center space-y-8 animate-in zoom-in-95 duration-500">
            <div className="relative w-24 h-24 mx-auto">
              <div className="absolute inset-0 border-8 border-emerald-100 rounded-full"></div>
              <div className="absolute inset-0 border-8 border-emerald-600 rounded-full border-t-transparent animate-spin"></div>
              <img src={ASSETS.LOGO_MONKEY} className="absolute inset-0 m-auto w-10 h-10 object-contain animate-pulse rounded-lg" alt="Monkey Load" />
            </div>
            <h3 className="text-2xl font-black text-slate-800">Scoring Match Fit</h3>
          </div>
        </div>
      )}

      {activeAnalysis && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[200] flex items-center justify-center p-4">
          <div className="bg-white p-8 rounded-[3rem] shadow-2xl max-w-lg w-full text-center space-y-6 animate-in zoom-in-95 duration-300 border-t-8 border-emerald-600">
            <div className="bg-emerald-50 p-4 rounded-full w-fit mx-auto"><ShieldCheck className="w-12 h-12 text-emerald-600" /></div>
            <h3 className="text-4xl font-black text-slate-800">{activeAnalysis.result.score}% Match!</h3>
            <p className="text-slate-600 leading-relaxed font-medium bg-slate-50 p-6 rounded-2xl border border-slate-100 italic">"{activeAnalysis.result.reasoning}"</p>
            <div className="flex gap-4">
              <button 
                onClick={() => {
                  const c = conversations.find(cv => cv.jobId === activeAnalysis.jobId);
                  if (c) {
                    setActiveConversationId(c.id);
                    setView('messages');
                  }
                  setActiveAnalysis(null);
                }}
                className="flex-1 py-4 bg-emerald-600 text-white font-black rounded-2xl hover:bg-emerald-700 transition-all shadow-xl active:scale-95"
              >
                Go to Chat
              </button>
              <button 
                onClick={() => setActiveAnalysis(null)}
                className="flex-1 py-4 bg-slate-100 text-slate-500 font-bold rounded-2xl hover:bg-slate-200"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ListChecks = ({ className }: { className?: string }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 17 2 2 4-4"/>
    <path d="m3 7 2 2 4-4"/>
    <path d="M13 6h8"/>
    <path d="M13 12h8"/>
    <path d="M13 18h8"/>
  </svg>
);


